// Човешки прочит на записите, които не са нито по сметка, нито складови:
// настройките в „Конфигурация", печатът на документи, гласовата поръчка,
// повторните фискални опити и отдалеченият ъпдейт на каса.
//
// Обединяващото между тях е, че стойността на записа е в ОТГОВОРА, не в
// заявката: печатът и MQTT командите връщат envelope `{request_id, status,
// error}`, а гласовата поръчка праща цялото меню (заявката винаги е отрязана)
// и връща разпознатото. Затова тук почти нищо не чете `request_body`.

import type { TFunction } from "i18next";
import type { AuditLogDetail } from "../../../../api/auditLogs";
import type { AuditLookups } from "./useAuditLookups";
import type { HumanStory } from "./story";
import { emptyStory, asArr, asObj, asString, field } from "./story";
import { qty, toNumber } from "./format";
import { CFG, ENVELOPE, FISCAL, MODULE_PATHS, VOICE } from "./contract";

/** Факти от envelope-а `{request_id, status, error}` на печат/MQTT команда. */
function pushEnvelopeFacts(
  story: HumanStory,
  envelope: unknown,
  t: TFunction,
): void {
  const object = asObj(envelope);
  if (!object) return;
  const status = asString(object[ENVELOPE.status]);
  if (status) {
    const failed = status === FISCAL.failedValue;
    story.facts.push({
      label: t("auditLogDetail.human.result"),
      value: failed
        ? t("auditLogDetail.human.failedLabel")
        : t("auditLogDetail.human.successLabel"),
      tone: failed ? "remove" : "add",
    });
  }
  const error = asString(object[ENVELOPE.error]);
  if (error) {
    story.facts.push({
      label: t("auditLogDetail.human.errorLabel"),
      value: error,
      tone: "remove",
      inline: true,
    });
  }
  const requestId = asString(object[ENVELOPE.requestId]);
  if (requestId) {
    story.facts.push({
      label: t("auditLogDetail.human.requestId"),
      value: requestId,
      inline: true,
    });
  }
}

/** Стойността на една настройка от `PUT /configuration/*`. */
function settingValue(body: unknown, t: TFunction): string | null {
  const object = asObj(body);
  if (!object) return null;
  if (typeof object[CFG.enabled] === "boolean") {
    return object[CFG.enabled]
      ? t("auditLogDetail.human.turnedOn")
      : t("auditLogDetail.human.turnedOff");
  }
  const view = asString(object[CFG.view]);
  if (view) return view;
  // Настройка с друга форма (нефискалното отчитане) — показваме първата
  // скаларна стойност, вместо да мълчим.
  for (const value of Object.values(object)) {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean") {
      return value
        ? t("auditLogDetail.human.turnedOn")
        : t("auditLogDetail.human.turnedOff");
    }
  }
  return null;
}

export function interpretSystem(
  kind: string,
  detail: AuditLogDetail,
  lookups: AuditLookups,
  t: TFunction,
): HumanStory | null {
  const human = (key: string) => t(`auditLogDetail.human.${key}`);

  switch (kind) {
    case "configSetting": {
      const story = emptyStory(kind, "info");
      const value = settingValue(detail.request_body, t);
      if (!value) {
        story.interpreted = false;
        return story;
      }
      story.facts.push({ label: human("newValue"), value });
      return story;
    }

    case "preliminaryPrint":
    case "invoicePrint":
    case "establishmentPrint": {
      const story = emptyStory(kind, "info");
      const documentId =
        detail.path.match(MODULE_PATHS.invoicePrint)?.[1] ??
        detail.path.match(MODULE_PATHS.establishmentPrint)?.[1] ??
        null;
      if (documentId) {
        story.facts.push({
          label: human(kind === "invoicePrint" ? "invoiceNo" : "reportNo"),
          value: `№ ${documentId}`,
        });
      }
      pushEnvelopeFacts(story, detail.response_body, t);
      story.interpreted = story.facts.length > 0;
      return story;
    }

    case "fiscalRetry":
    case "stornoRetry": {
      const story = emptyStory(kind, "money");
      // Отговорът е `{bill_id, fiscal_result: {...}}` и при двата пътя.
      pushEnvelopeFacts(story, field(detail.response_body, "fiscal_result"), t);
      story.note = human(
        kind === "stornoRetry" ? "stornoRetryNote" : "fiscalRetryNote",
      );
      return story;
    }

    case "deviceUpdate": {
      const story = emptyStory(kind, "info");
      const deviceId = detail.path.match(MODULE_PATHS.deviceFiscalUpdate)?.[1];
      if (deviceId) {
        story.facts.push({ label: human("device"), value: `№ ${deviceId}` });
      }
      pushEnvelopeFacts(story, detail.response_body, t);
      story.note = human("deviceUpdateNote");
      return story;
    }

    case "voiceOrder": {
      const story = emptyStory(kind, "info");
      const response = asObj(detail.response_body);
      // Заявката носи цялото меню и на практика винаги е отрязана — но
      // изреченото пак се вижда, ако е останало под тавана.
      const transcript = asString(field(detail.request_body, VOICE.transcript));
      const tableName = asString(response?.[VOICE.tableName]);
      if (tableName) {
        story.facts.push({ label: human("table"), value: tableName });
      }
      story.lines = asArr(response?.[VOICE.items]).map((raw) => {
        const item = asObj(raw) ?? {};
        const candidates = asArr(item[VOICE.productIds]);
        const spoken = asString(item[VOICE.spokenName]) ?? "—";
        // Един кандидат = сигурно съвпадение и показваме името от менюто;
        // няколко = двусмислено, нула = непознат продукт (BE конвенцията).
        const name =
          candidates.length === 1
            ? lookups.itemName(candidates[0])
            : spoken;
        const subs: string[] = [];
        if (candidates.length > 1) {
          subs.push(`${human("voiceAmbiguous")}: ${candidates.length}`);
        } else if (candidates.length === 0) {
          subs.push(human("voiceNoMatch"));
        }
        const comment = asString(item[VOICE.comment]);
        if (comment) subs.push(comment);
        return {
          qty: qty(toNumber(item[VOICE.quantity]) ?? 1),
          name,
          subs: subs.length > 0 ? subs : undefined,
          tone: candidates.length === 1 ? ("add" as const) : ("neutral" as const),
        };
      });
      story.linesLabel = human("recognizedProducts");
      if (transcript) story.note = `„${transcript}"`;
      story.interpreted = story.lines.length > 0 || story.facts.length > 0;
      return story;
    }

    default:
      return null;
  }
}
